import { useCallback, useMemo } from "react";
import { useLocalStorage } from "../../../../utils/useLocalStorage";
import {
  SelectableCustomColumn,
  ProductPresenceColumn,
  VendorPresenceColumn,
  ProductPresenceDataType,
  VendorPresenceDataType,
  ChooseColumnDialogPhases,
  CUSTOM_COLUMN_LOCAL_STORAGE_KEY,
} from "../types";

const generateColumnId = (type: SelectableCustomColumn["type"]) =>
  `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const useCustomColumns = () => {
  const [customColumns, setCustomColumns] = useLocalStorage<
    SelectableCustomColumn[]
  >(CUSTOM_COLUMN_LOCAL_STORAGE_KEY, []);

  const addCustomColumn = useCallback(
    (column: Omit<SelectableCustomColumn, "id">) => {
      const newColumn: SelectableCustomColumn = {
        ...column,
        id: generateColumnId(column.type),
      };
      setCustomColumns((prev) => [...prev, newColumn]);
      return newColumn.id;
    },
    [setCustomColumns]
  );

  const replaceColumns = useCallback(
    (
      columns: (Omit<SelectableCustomColumn, "id" | "phase"> & {
        phase?: ChooseColumnDialogPhases;
      })[]
    ) => {
      setCustomColumns(
        columns.map((column) => ({
          ...column,
          phase: column.phase ?? ChooseColumnDialogPhases.CHOOSE_TYPE,
          id: generateColumnId(column.type),
        }))
      );
    },
    [setCustomColumns]
  );

  const updateCustomColumn = useCallback(
    (id: string, updates: Partial<SelectableCustomColumn>) => {
      setCustomColumns((prev) =>
        prev.map((column) =>
          column.id === id ? { ...column, ...updates } : column
        )
      );
    },
    [setCustomColumns]
  );

  const deleteCustomColumn = useCallback(
    (id: string) => {
      setCustomColumns((prev) => prev.filter((column) => column.id !== id));
    },
    [setCustomColumns]
  );

  const toggleColumnSelection = useCallback(
    (id: string) => {
      setCustomColumns((prev) =>
        prev.map((column) =>
          column.id === id
            ? { ...column, isSelected: !column.isSelected }
            : column
        )
      );
    },
    [setCustomColumns]
  );

  const selectedColumns = useMemo(
    () => customColumns.filter((column) => column.isSelected),
    [customColumns]
  );

  const getProductPresenceColumns = useCallback((): ProductPresenceColumn[] => {
    return selectedColumns
      .filter((column) => column.type === "product")
      .map((column) => ({
        dataType: column.dataType as ProductPresenceDataType,
        name: column.name,
        ids: column.ids,
      }));
  }, [selectedColumns]);

  const getVendorPresenceColumns = useCallback((): VendorPresenceColumn[] => {
    return selectedColumns
      .filter((column) => column.type === "vendor")
      .map((column) => ({
        dataType: column.dataType as VendorPresenceDataType,
        name: column.name,
        ids: column.ids,
      }));
  }, [selectedColumns]);

  return {
    customColumns,
    selectedColumns,
    addCustomColumn,
    updateCustomColumn,
    deleteCustomColumn,
    toggleColumnSelection,
    replaceColumns,
    getProductPresenceColumns,
    getVendorPresenceColumns,
  };
};
